import { GlobeIcon, HouseIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { BreadcrumbItem, Breadcrumbs, Container, Heading, Navbar } from '../../components'
import { useCollection, useCollectionTemplatesPage, useTitle } from '../../hooks'
import { useMemo, useState } from 'react'
import { SearchBadge, SearchBar, TemplateCard } from '@indigohive/templates-gallery-components'
import { MAP_LOGO_BY_PROVIDER } from '../../components/__common/map-logo-by-provider'
import { Link, useSearchParams } from 'react-router-dom'
import { UUID } from '@indigohive/cogfy-types'
import { Provider } from '@indigohive/cogfy-providers'

export function CollectionTemplatesPage () {
  const { t } = useTranslation()
  useTitle({ title: t('Templates') })
  const [search, setSearch] = useState('')
  const [searchParams] = useSearchParams()
  const parent = useCollection(searchParams.get('folder') as UUID | undefined)
  const getTemplatesPage = useCollectionTemplatesPage({ pageSize: 30 })
  const templates = getTemplatesPage.data?.data ?? []

  const [selectedTag, setSelectedTag] = useState('')

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: t('Home'),
      icon: <HouseIcon size={16} />
    },
    {
      label: t('Templates'),
      icon: <GlobeIcon size={16} />
    }
  ]

  const filteredTemplates = useMemo(() => {
    if (!search && !selectedTag) {
      return templates
    }

    const filtered = templates.filter(template => {
      const matchesName = template.name.toLowerCase().includes(search.toLowerCase())
      const matchesTag = selectedTag ? template.tags?.some(tag => tag.name === selectedTag) : true

      return matchesName && matchesTag
    })

    return filtered
  }, [templates, search, selectedTag])

  const uniqueTags = useMemo(() => {
    const allTags = templates.flatMap(template => template.tags ?? [])
    const uniqueTags = Array.from(new Set(allTags.map(tag => tag.name)))

    return uniqueTags
  }, [templates])

  return (
    <>
      <div className="relative z-0">
        <Navbar className="!bg-transparent !shadow-none px-6">
          <Breadcrumbs items={breadcrumbItems} />
        </Navbar>
        <div className="absolute inset-0 h-64 bg-gradient-to-b from-primary opacity-30 pointer-events-none" />
      </div>

      <Container className="relative px-6 z-10 my-14">
        <div className='flex flex-col gap-2'>
          <Heading className="flex gap-4 justify-center items-center max-w-screen-xl mx-auto">
            <div className="flex flex-col gap-3">
              <p className="text-center text-4xl">Templates</p>
              <p className="text-sm font-light text-center">
                {t('collectionTemplatesPage:Ready-made templates to optimize your collection and flow creation in Cogfy')}
              </p>
              <SearchBar onChange={setSearch} value={search} />
              <div className='flex flex-wrap gap-2 justify-center mx-6 md:mx-auto'>
                {uniqueTags.map((tag, index) => (
                  <SearchBadge
                    key={index}
                    label={tag}
                    onClickSelected={() => setSelectedTag('')}
                    onClickUnselected={() => setSelectedTag(tag)}
                    isSelected={selectedTag === tag}
                  />
                ))}
              </div>
            </div>
          </Heading>
        </div>

        {getTemplatesPage.isLoading && <progress className="progress w-full" />}

        <div className="my-28">
          <div className='flex justify-end items-end gap-4 mb-8'>
            {getTemplatesPage.data && (
              <span className="text-sm opacity-50">
                {filteredTemplates.length === 0 && 'No templates found'}
                {filteredTemplates.length === 1 && `Found ${filteredTemplates.length} template`}
                {filteredTemplates.length > 1 && `Found ${filteredTemplates.length} templates`}
              </span>
            )}
          </div>
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                providers={
                  template.providers
                    ?.map(provider => (
                      {
                        name: provider.label as Provider['name'],
                        image: MAP_LOGO_BY_PROVIDER[provider.name] ?? ''
                      }
                    ))}
                Anchor={Link}
                AnchorProps={
                  {
                    to: `./${parent.data?.id
                  ? `${template?.slug}?folder=${parent.data?.id}`
                  : `${template?.slug}`}`
                  }
                }
              />
            ))}
          </div>
        </div>
      </Container>
    </>
  )
}
